import { TEXT_BODY, TEXT_MUTED, TEXT_PRIMARY } from '../../lib/medievalTheme';
import { ELEMENT_ORDER, SORCERY_CHART_COLORS } from '../../utils/sorcery/deckMetrics';
import ElementIcon from './ElementIcon';
import StatCard from './StatCard';
import { noiseBarStyle } from './ChartNoise';
import { t } from '../../localization/t';

function AffinityRow({ element, sites, pips, elementSum, max }) {
  const pct = elementSum === 0 ? 0 : Math.round((sites / elementSum) * 100);
  const barPct = max === 0 ? 0 : (sites / max) * 100;
  const muted = sites === 0;
  const color = SORCERY_CHART_COLORS[element];
  // Show "N pips" separately only when a site supplies 2+ pips of
  // the same element (so pips > sites). Otherwise it's redundant.
  const pipNote = sites > 0 && pips > sites ? ` · ${t('{n} pips', { n: pips })}` : '';

  return (
    <div className="flex items-center gap-2 text-sm">
      <div className="flex items-center gap-1.5 w-20 shrink-0">
        <ElementIcon element={element} size={18} muted={muted} />
        <span
          className="font-medium text-xs"
          style={{ color: muted ? TEXT_MUTED : TEXT_PRIMARY }}
        >
          {t(element)}
        </span>
      </div>
      <div className="flex-1 h-2.5 rounded-full overflow-hidden min-w-0" style={{
        background: 'rgba(0,0,0,0.4)',
        border: '1px solid rgba(180,140,60,0.1)',
      }}>
        <div
          className="h-full rounded-full transition-[width] duration-200"
          style={{
            width: `${barPct}%`,
            ...(muted
              ? { background: 'transparent' }
              : noiseBarStyle(`linear-gradient(90deg, ${color}dd 0%, ${color}88 100%)`)),
          }}
        />
      </div>
      <div className="flex items-center gap-1.5 text-[0.6875cqw] tabular-nums justify-end shrink-0" style={{ minWidth: '4.5cqw' }}>
        <span style={{ color: muted ? TEXT_MUTED : TEXT_PRIMARY }}>
          {sites}
          {pipNote ? <span style={{ color: TEXT_MUTED }}>{pipNote}</span> : null}
        </span>
        <span className="w-8 text-right" style={{ color: TEXT_MUTED }}>{pct}%</span>
      </div>
    </div>
  );
}

function FooterStat({ label, value, hint }) {
  return (
    <div className="flex flex-col">
      <span className="text-[0.625cqw] font-semibold" style={{ color: TEXT_MUTED }}>
        {label}
      </span>
      <span className="text-sm font-bold tabular-nums" style={{ color: TEXT_PRIMARY }}>
        {value}
      </span>
      {hint ? (
        <span className="text-[0.625cqw]" style={{ color: TEXT_BODY, opacity: 0.7 }}>
          {hint}
        </span>
      ) : null}
    </div>
  );
}

export function AtlasCompositionCard({ metrics, className }) {
  const { total, multiElement, utility, sitesByElement, pipsByElement } = metrics.atlas;
  const maxSites = Math.max(1, ...ELEMENT_ORDER.map((el) => sitesByElement[el] || 0));
  // Denominator for the per-row percentage: the sum of element affinities
  // across the atlas. A multi-element site contributes to multiple rows, so
  // using `total` (unique site count) made the percentages sum to >100%.
  // Summing the per-element site counts instead gives a true share-of-mix
  // that adds up to 100%.
  const elementSum = ELEMENT_ORDER.reduce((acc, el) => acc + (sitesByElement[el] || 0), 0);
  const affinitySites = total - utility;

  if (total === 0) {
    return (
      <StatCard
        title={t('Atlas Composition')}
        description={t('Sites by element affinity.')}
        className={className}
      >
        <div className="text-sm py-4" style={{ color: TEXT_BODY, opacity: 0.7 }}>
          {t('No sites in atlas yet.')}
        </div>
      </StatCard>
    );
  }

  return (
    <StatCard
      title={t('Atlas Composition')}
      description={t('Sites by element affinity.')}
      className={className}
    >
      <div className="flex flex-col gap-2.5">
        {ELEMENT_ORDER.map((element) => (
          <AffinityRow
            key={element}
            element={element}
            sites={sitesByElement[element] || 0}
            pips={pipsByElement[element] || 0}
            elementSum={elementSum}
            max={maxSites}
          />
        ))}
      </div>

      <div
        className="mt-4 pt-3 grid grid-cols-1 md:grid-cols-3 gap-3"
        style={{ borderTop: '1px solid rgba(180,140,60,0.15)' }}
      >
        <FooterStat
          label={t('With affinity')}
          value={`${affinitySites} / ${total}`}
          hint={t('sites supplying ≥1 pip')}
        />
        <FooterStat
          label={t('Multi-element')}
          value={multiElement}
          hint={t('sites supplying 2+ elements')}
        />
        <FooterStat
          label={t('Utility sites')}
          value={utility}
          hint={t('no threshold supplied')}
        />
      </div>
    </StatCard>
  );
}
